import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FileOpenIcon from '@mui/icons-material/FileOpen';
import PublishedWithChangesIcon from '@mui/icons-material/PublishedWithChanges';
import Page from 'src/components/Page';
import SkeletonBlock from 'src/components/SkeletonBlock';
import StatusBadge from 'src/components/StatusBadge';
import DocumentViewerDialog from 'src/components/DocumentViewer/DocumentViewerDialog';
import SendEmailDialog from 'src/views/taxAccounting/CFDIIssued/SendEmailDialog';
import AssignTransactionToContractDialog from 'src/components/AccountStatement/AssignTransactionToContractDialog';
import {
  selectTransactionDetail,
  selectTransactionDetailComplements,
  selectTransactionDetailRelatedInvoices,
  selectTransactionDetailError
} from 'src/store/selectors/transactionSelectors';
import {
  LOAD_TRANSACTION_DETAIL,
  loadTransactionDetail,
  clearTransactionDetail,
  assignTransactionToContract
} from 'src/store/actions/transactionActions';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import { TransactionsType } from 'src/utils/constants/transactions';
import { PaymentMethod } from 'src/utils/constants/cfdi';
import { clientDetailTabsIndex } from 'src/utils/constants/clients';
import dateFormatType from 'src/utils/dateFormatType';
import selectFormatDate from 'src/utils/selectDateFormat';
import { formatPositiveAmount } from 'src/utils/formatAmout';
import InvoiceFields from './InvoiceFields';
import InvoiceSummaryStats from './InvoiceSummaryStats';
import PaymentComplementsList from './PaymentComplementsList';
import PaymentFields from './PaymentFields';
import RelatedInvoicesList from './RelatedInvoicesList';
import {
  MUTED_LABEL_COLOR,
  HEADING_TEXT_COLOR,
  HAIRLINE_BORDER_COLOR,
  CARD_BORDER_COLOR,
  BTN_BORDER_COLOR,
  BREADCRUMB_TEXT_COLOR,
  BREADCRUMB_SEPARATOR_COLOR
} from './styleConstants';

const PAYMENT_STATUS_TONE = {
  PAID: 'success',
  PARTIAL: 'info',
  PENDING: 'warning'
};

const CARD_SX = {
  border: `1px solid ${CARD_BORDER_COLOR}`,
  borderRadius: '4px',
  backgroundColor: '#ffffff',
  boxShadow: '0 1px 2px rgba(15, 32, 49, 0.08)'
};

const CARD_HEAD_SX = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '16px',
  minHeight: '64px',
  padding: '0 20px',
  borderBottom: `1px solid ${HAIRLINE_BORDER_COLOR}`
};

const CARD_TITLE_SX = {
  color: HEADING_TEXT_COLOR,
  fontSize: '17px',
  fontWeight: 800
};

const BTN_SX = {
  minHeight: '38px',
  border: `1px solid ${BTN_BORDER_COLOR}`,
  borderRadius: '4px',
  backgroundColor: '#ffffff',
  color: 'primary.main',
  fontSize: '12px',
  fontWeight: 800,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  boxShadow: '0 1px 2px rgba(15, 32, 49, 0.08)'
};

const Breadcrumb = ({ clientName, folio }) => {
  const { t } = useTranslation();
  const items = [t('NAVIGATION.CLIENT'), clientName, t('NAVIGATION.TRANSACTIONS'), folio];

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '10px',
        color: BREADCRUMB_TEXT_COLOR,
        fontSize: '12px',
        fontWeight: 700,
        marginBottom: '26px'
      }}
    >
      {items.map((item, index) => (
        <React.Fragment key={item ?? index}>
          <Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 'inherit', color: 'inherit' }}>
            {item || t('COMMON.LOADING')}
          </Typography>
          {index < items.length - 1 && (
            <Typography component="span" sx={{ color: BREADCRUMB_SEPARATOR_COLOR }}>
              /
            </Typography>
          )}
        </React.Fragment>
      ))}
    </Box>
  );
};

Breadcrumb.propTypes = {
  clientName: PropTypes.string,
  folio: PropTypes.string
};

Breadcrumb.defaultProps = {
  clientName: null,
  folio: null
};

const TransactionDetailView = () => {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const detail = useSelector(selectTransactionDetail);
  const complements = useSelector(selectTransactionDetailComplements);
  const relatedInvoices = useSelector(selectTransactionDetailRelatedInvoices);
  const relatedInvoicesCountLabel =
    relatedInvoices.length === 1
      ? `1 ${t('TRANSACTION.INVOICE_SINGULAR')}`
      : `${relatedInvoices.length} ${t('TRANSACTION.INVOICE_PLURAL')}`;
  const hasError = useSelector(selectTransactionDetailError);
  const loading = useSelector(isLoadingSelector([LOAD_TRANSACTION_DETAIL.action]));

  const [viewingDocument, setViewingDocument] = useState(null);
  const [sendingEmailForInvoiceId, setSendingEmailForInvoiceId] = useState(null);
  const [assigningContract, setAssigningContract] = useState(false);

  useEffect(() => {
    dispatch(loadTransactionDetail(Number(invoiceId)));
    return () => {
      dispatch(clearTransactionDetail());
    };
  }, [dispatch, invoiceId]);

  const complementsById = useMemo(() => {
    const byId = {};
    complements.forEach(complement => {
      byId[complement.id] = complement;
    });
    return byId;
  }, [complements]);

  const handleRetry = () => {
    dispatch(loadTransactionDetail(Number(invoiceId)));
  };

  const handleViewPdf = id => {
    const source = detail && id === detail.id ? detail : complementsById[id];
    if (source?.document?.documentUrl) {
      setViewingDocument({
        documentUrl: source.document.documentUrl,
        fileName: source.folio
      });
    }
  };

  const handleSendToClient = id => {
    const source = detail && id === detail.id ? detail : complementsById[id];
    if (source?.invoiceDbId) {
      setSendingEmailForInvoiceId(source.invoiceDbId);
    }
  };

  const handleAssignToContract = values => {
    dispatch(
      assignTransactionToContract(detail.id, values, () => {
        setAssigningContract(false);
        handleRetry();
      })
    );
  };

  return (
    <Page title={detail ? `${detail.type === TransactionsType.PAYMENT ? t('TRANSACTION.DETAIL.PAYMENT_TITLE') : t('TRANSACTION.DETAIL.TITLE')} ${detail.folio}` : ''}>
      <Container maxWidth={false} sx={{ padding: '28px 32px 48px' }}>
        <Breadcrumb clientName={detail?.client} folio={detail?.folio} />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '20px',
            marginBottom: '22px',
            flexWrap: 'wrap'
          }}
        >
          <Button
            onClick={() =>
              navigate(
                detail
                  ? `/clients/detail/${detail.clientId}/${clientDetailTabsIndex.ACCOUNTING_STATE}`
                  : -1
              )
            }
            startIcon={<ArrowBackIcon />}
            sx={{
              color: 'primary.main',
              fontSize: '13px',
              fontWeight: 800,
              letterSpacing: '0.06em',
              textTransform: 'uppercase'
            }}
          >
            {t('TRANSACTION.DETAIL.BACK_TO_TRANSACTIONS')}
          </Button>
          {detail && (
            <Box sx={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                startIcon={<FileOpenIcon />}
                disabled={!detail.document?.documentUrl}
                onClick={() => handleViewPdf(detail.id)}
                sx={BTN_SX}
              >
                {detail.type === TransactionsType.PAYMENT
                  ? t('TRANSACTION.DETAIL.VIEW_COMPLEMENT_PDF')
                  : t('TRANSACTION.DETAIL.VIEW_INVOICE_PDF')}
              </Button>
              {detail.type === TransactionsType.PAYMENT && (
                <Button
                  variant="outlined"
                  onClick={() => handleSendToClient(detail.id)}
                  sx={BTN_SX}
                >
                  {t('TRANSACTION.DETAIL.SEND_TO_CLIENT')}
                </Button>
              )}
              {!detail.contractId && (
                <Button
                  variant="outlined"
                  startIcon={<PublishedWithChangesIcon />}
                  onClick={() => setAssigningContract(true)}
                  sx={BTN_SX}
                >
                  {t('TRANSACTION.ASSOCIATE_TRANSACTION_TO_CONTRACT')}
                </Button>
              )}
            </Box>
          )}
        </Box>

        {hasError ? (
          <Box sx={{ display: 'grid', gap: '14px' }}>
            <Typography color="error">{t('TRANSACTION.DETAIL.LOAD_ERROR')}</Typography>
            <Button variant="outlined" onClick={handleRetry} sx={{ justifySelf: 'start' }}>
              {t('TRANSACTION.DETAIL.RETRY')}
            </Button>
          </Box>
        ) : loading || !detail ? (
          <Box sx={{ display: 'grid', gap: '18px' }}>
            <SkeletonBlock width="short" />
            <SkeletonBlock width="medium" tall />
            <SkeletonBlock width="medium" />
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1fr) auto',
                gap: '24px',
                alignItems: 'end',
                padding: '28px 30px',
                marginBottom: '18px',
                ...CARD_SX
              }}
            >
              <Box>
                <Typography
                  sx={{
                    color: MUTED_LABEL_COLOR,
                    fontSize: '12px',
                    fontWeight: 800,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase'
                  }}
                >
                  {detail.type === TransactionsType.PAYMENT
                    ? t('TRANSACTION.DETAIL.PAYMENT_TITLE')
                    : t('TRANSACTION.DETAIL.TITLE')}
                </Typography>
                <Typography
                  sx={{
                    margin: '7px 0 12px',
                    color: HEADING_TEXT_COLOR,
                    fontSize: 'clamp(30px, 4vw, 46px)',
                    lineHeight: 1.05,
                    fontWeight: 500,
                    letterSpacing: '-0.04em'
                  }}
                >
                  {detail.folio}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    gap: '8px',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    color: MUTED_LABEL_COLOR
                  }}
                >
                  {detail.type === TransactionsType.PAYMENT ? (
                    <StatusBadge tone="success">
                      {t('TRANSACTION.DETAIL.PAYMENT_APPLIED')}
                    </StatusBadge>
                  ) : (
                    <>
                      {detail.paymentMethod && (
                        <StatusBadge
                          tone={detail.paymentMethod === PaymentMethod.PPD ? 'info' : 'neutral'}
                        >
                          {detail.paymentMethod}
                        </StatusBadge>
                      )}
                      {detail.paymentStatus && (
                        <StatusBadge tone={PAYMENT_STATUS_TONE[detail.paymentStatus]}>
                          {t(`TRANSACTION.PAYMENT_STATUS.${detail.paymentStatus}`)}
                        </StatusBadge>
                      )}
                    </>
                  )}
                  <Typography component="span" sx={{ fontSize: 'inherit' }}>
                    {selectFormatDate(detail.date, dateFormatType.DAY)}
                    {detail.type === TransactionsType.PAYMENT
                      ? detail.origin
                        ? ` · ${t(`TRANSACTION.ORIGIN.${detail.origin}`)}`
                        : ''
                      : detail.contractFolioWithoutPrefix
                      ? ` · ${detail.contractFolioWithoutPrefix}`
                      : ''}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ minWidth: '220px', textAlign: 'right' }}>
                <Typography
                  component="span"
                  sx={{
                    display: 'block',
                    marginBottom: '5px',
                    color: MUTED_LABEL_COLOR,
                    fontSize: '11px',
                    fontWeight: 800,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase'
                  }}
                >
                  {detail.type === TransactionsType.PAYMENT
                    ? t('TRANSACTION.DETAIL.PAYMENT_AMOUNT_LABEL')
                    : t('TRANSACTION.DETAIL.INVOICE_AMOUNT')}
                </Typography>
                <Typography
                  component="strong"
                  sx={{
                    color: HEADING_TEXT_COLOR,
                    fontSize: '30px',
                    fontWeight: 800,
                    fontVariantNumeric: 'tabular-nums'
                  }}
                >
                  {formatPositiveAmount(detail.amountIvaIncluded)}
                </Typography>
              </Box>
            </Box>

            {detail.type === TransactionsType.PAYMENT ? (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                  gap: '18px',
                  alignItems: 'start'
                }}
              >
                <Box sx={{ ...CARD_SX, overflow: 'hidden' }}>
                  <Box sx={CARD_HEAD_SX}>
                    <Typography sx={CARD_TITLE_SX}>
                      {t('TRANSACTION.DETAIL.PAYMENT_DATA')}
                    </Typography>
                  </Box>
                  <PaymentFields
                    client={detail.client}
                    contractFolioWithoutPrefix={detail.contractFolioWithoutPrefix}
                    date={detail.date}
                    origin={detail.origin}
                    associatedInvoiceCount={relatedInvoices.length}
                    amountIvaIncluded={detail.amountIvaIncluded}
                    comment={detail.comment}
                    hasContract={Boolean(detail.contractId)}
                  />
                </Box>

                <Box sx={{ ...CARD_SX, overflow: 'hidden' }}>
                  <Box sx={CARD_HEAD_SX}>
                    <Typography sx={CARD_TITLE_SX}>
                      {t('TRANSACTION.DETAIL.ASSOCIATED_INVOICES')}
                    </Typography>
                    <StatusBadge tone="info">{relatedInvoicesCountLabel}</StatusBadge>
                  </Box>
                  <RelatedInvoicesList relatedInvoices={relatedInvoices} />
                </Box>
              </Box>
            ) : (
              <>
                <InvoiceSummaryStats
                  amountIvaIncluded={detail.amountIvaIncluded}
                  pendingAmount={detail.pendingAmount}
                />

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1.05fr) minmax(360px, 0.95fr)',
                    gap: '18px',
                    alignItems: 'start'
                  }}
                >
                  <Box sx={{ ...CARD_SX, overflow: 'hidden' }}>
                    <Box sx={CARD_HEAD_SX}>
                      <Typography sx={CARD_TITLE_SX}>
                        {t('TRANSACTION.DETAIL.INVOICE_DATA')}
                      </Typography>
                    </Box>
                    <InvoiceFields
                      client={detail.client}
                      contractFolioWithoutPrefix={detail.contractFolioWithoutPrefix}
                      date={detail.date}
                      origin={detail.origin}
                      categoryDescription={detail.categoryDescription}
                      paymentMethod={detail.paymentMethod}
                      comment={detail.comment}
                      invoiceUUID={detail.invoiceUUID}
                    />
                  </Box>

                  <Box sx={{ ...CARD_SX, overflow: 'hidden' }}>
                    <Box sx={CARD_HEAD_SX}>
                      <Typography sx={CARD_TITLE_SX}>
                        {t('TRANSACTION.DETAIL.REGISTERED_PAYMENTS')}
                      </Typography>
                      <StatusBadge tone="info">
                        {complements.length === 1
                          ? `1 ${t('TRANSACTION.COMPLEMENT_SINGULAR')}`
                          : `${complements.length} ${t('TRANSACTION.COMPLEMENT_PLURAL')}`}
                      </StatusBadge>
                    </Box>
                    <PaymentComplementsList
                      complements={complements}
                      onViewPdf={handleViewPdf}
                      onSendToClient={handleSendToClient}
                      onAssociateContract={() => setAssigningContract(true)}
                    />
                  </Box>
                </Box>
              </>
            )}
          </>
        )}
      </Container>

      {viewingDocument && (
        <DocumentViewerDialog
          open={Boolean(viewingDocument)}
          onClose={() => setViewingDocument(null)}
          documentUrl={viewingDocument.documentUrl}
          fileName={viewingDocument.fileName}
        />
      )}

      {sendingEmailForInvoiceId && detail && (
        <SendEmailDialog
          open={Boolean(sendingEmailForInvoiceId)}
          onClose={() => setSendingEmailForInvoiceId(null)}
          id={sendingEmailForInvoiceId}
          clientId={detail.clientId}
        />
      )}

      {assigningContract && detail && (
        <AssignTransactionToContractDialog
          open={assigningContract}
          transaction={{ clientId: detail.clientId }}
          handleAssignInvoiceToContract={handleAssignToContract}
          handleClose={() => setAssigningContract(false)}
        />
      )}
    </Page>
  );
};

export default TransactionDetailView;
